import { INITIAL_TO_GAME, SubmitMode } from '../persistence/types.js';
import { getGameTitle } from '../games/GameRegistry.js';
import { ApiClient } from '../api/ApiClient.js';
import { LeaderboardEntry } from '../shared/apiTypes.js';
import { cap, isReservedUsername } from '../shared/scoring.js';

export interface LeaderboardChallenge {
    id: number;
    title: string;
    scope: string;   // sorted game initials in scope
    size: number;    // grid size, for the per-game penalty ceiling cap(size)
}

/** A leaderboard row paired with the score for the active tab. */
interface ScoredRow {
    entry: LeaderboardEntry;
    score: number;
}

export interface LeaderboardCallbacks {
    getSubmitMode: () => SubmitMode;
    setSubmitMode: (mode: SubmitMode) => void;
    /** Re-submit the current grid's solution (so a freshly-set username attaches). */
    onSubmitNow: () => Promise<void>;
}

const SUBMIT_MODES: Array<{ value: SubmitMode; label: string }> = [
    { value: 'manual', label: 'Manual' },
    { value: 'each', label: 'Each game' },
    { value: 'full', label: 'Full wins' },
];

/**
 * Challenge leaderboard overlay: a Total tab plus one tab per scope game. Shows
 * the current player's row highlighted, lets them set a username (empty = shown as
 * italic "Anonymous"), pick a submission mode, and submit on demand.
 */
export class Leaderboard {
    private containerEl: HTMLElement;
    private callbacks: LeaderboardCallbacks;
    private challenge: LeaderboardChallenge | null = null;
    private entries: LeaderboardEntry[] = [];
    private activeTab = 'total';   // 'total' or a game initial
    private loading = false;
    /** Tie groups the player has expanded, keyed by `${tab}:${score}`. */
    private expandedTies = new Set<string>();

    constructor(containerEl: HTMLElement, callbacks: LeaderboardCallbacks) {
        this.containerEl = containerEl;
        this.callbacks = callbacks;
    }

    async show(challenge: LeaderboardChallenge): Promise<void> {
        this.challenge = challenge;
        this.activeTab = 'total';
        this.expandedTies.clear();
        this.containerEl.classList.remove('hidden');
        await this.refresh();
    }

    hide(): void {
        this.containerEl.classList.add('hidden');
    }

    private async refresh(): Promise<void> {
        if (!this.challenge) return;
        this.loading = true;
        this.render();
        const board = await ApiClient.getLeaderboard(this.challenge.id);
        this.entries = board?.entries ?? [];
        this.loading = false;
        this.render();
    }

    /** Parse a "A:405 B:308 F:3600" breakdown into an initial→score map. */
    private parseBreakdown(breakdown: string): Record<string, number> {
        const out: Record<string, number> = {};
        for (const part of breakdown.split(/\s+/)) {
            const [initial, score] = part.split(':');
            if (initial && score !== undefined) out[initial] = Number(score);
        }
        return out;
    }

    private render(): void {
        const el = this.containerEl;
        el.innerHTML = '';
        if (!this.challenge) return;

        const title = document.createElement('h1');
        title.className = 'leaderboard-title';
        title.textContent = `Challenge for ${this.challenge.title}`;
        el.appendChild(title);

        el.appendChild(this.renderControls());

        // Per-game tabs exist only for games you've submitted a win for; otherwise
        // fall back to the always-available Total tab.
        const wins = this.myWins();
        if (this.activeTab !== 'total' && !wins.has(this.activeTab)) this.activeTab = 'total';
        el.appendChild(this.renderTabs(wins));

        if (this.loading) {
            const loading = document.createElement('div');
            loading.className = 'leaderboard-empty';
            loading.textContent = 'Loading…';
            el.appendChild(loading);
            return;
        }
        el.appendChild(this.renderTable());
    }

    private renderControls(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'leaderboard-controls';

        // Username row
        const nameRow = document.createElement('div');
        nameRow.className = 'leaderboard-name-row';
        const input = document.createElement('input');
        input.className = 'leaderboard-name-input';
        input.type = 'text';
        input.maxLength = 24;
        input.placeholder = 'Anonymous';
        input.value = ApiClient.getUsername();
        const save = document.createElement('button');
        save.className = 'leaderboard-btn-action';
        save.textContent = 'Save name';
        // Saving a name never submits — it just attaches to your next submission.
        save.addEventListener('click', () => {
            ApiClient.setUsername(input.value.trim());
            this.render();
        });
        nameRow.append(this.label('Your name'), input, save);
        wrap.appendChild(nameRow);

        // Submission mode + submit-now
        const modeRow = document.createElement('div');
        modeRow.className = 'leaderboard-mode-row';
        const seg = document.createElement('div');
        seg.className = 'settings-segmented';
        const current = this.callbacks.getSubmitMode();
        for (const opt of SUBMIT_MODES) {
            const b = document.createElement('button');
            b.className = 'settings-segment';
            b.textContent = opt.label;
            b.classList.toggle('active', opt.value === current);
            b.addEventListener('click', () => {
                if (this.callbacks.getSubmitMode() === opt.value) return;
                this.callbacks.setSubmitMode(opt.value);
                this.render();
            });
            seg.appendChild(b);
        }
        const submit = document.createElement('button');
        submit.className = 'leaderboard-btn-action';
        submit.textContent = 'Submit now';
        submit.addEventListener('click', async () => {
            await this.callbacks.onSubmitNow();
            await this.refresh();
        });
        modeRow.append(this.label('Submit'), seg, submit);
        wrap.appendChild(modeRow);

        return wrap;
    }

    private renderTabs(wins: Set<string>): HTMLElement {
        const tabs = document.createElement('div');
        tabs.className = 'leaderboard-tabs';

        const make = (key: string, text: string) => {
            const t = document.createElement('button');
            t.className = 'leaderboard-tab';
            t.textContent = text;
            t.classList.toggle('active', this.activeTab === key);
            t.addEventListener('click', () => { this.activeTab = key; this.render(); });
            return t;
        };

        tabs.appendChild(make('total', 'Total'));
        for (const initial of this.challenge?.scope ?? '') {
            if (!wins.has(initial)) continue; // only games you've submitted a win for
            const gameType = INITIAL_TO_GAME[initial];
            tabs.appendChild(make(initial, gameType ? getGameTitle(gameType) : initial));
        }
        return tabs;
    }

    /** Scope games the current player has *submitted* a win for (drives which tabs show). */
    private myWins(): Set<string> {
        const mine = this.entries.find(e => e.user_id === ApiClient.getToken());
        if (!mine || !this.challenge) return new Set();
        const capVal = cap(this.challenge.size);
        const breakdown = this.parseBreakdown(mine.score_breakdown);
        return new Set([...this.challenge.scope].filter(ini => {
            const s = breakdown[ini];
            return s !== undefined && s < capVal; // a real win, not a penalty
        }));
    }

    private renderTable(): HTMLElement {
        const me = ApiClient.getToken();
        const isTotal = this.activeTab === 'total';
        const capVal = cap(this.challenge?.size ?? 0);

        let rows: ScoredRow[] = this.entries
            .map(e => ({
                entry: e,
                score: isTotal ? e.total_score : this.parseBreakdown(e.score_breakdown)[this.activeTab],
            }))
            .filter(r => r.score !== undefined);
        // Per-game tabs list only players who actually solved the game (hide penalties).
        if (!isTotal) rows = rows.filter(r => r.score < capVal);
        // Ties broken by earliest submission (matches the backend's ORDER BY).
        rows.sort((a, b) => a.score - b.score || a.entry.submitted_at.localeCompare(b.entry.submitted_at));

        if (rows.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'leaderboard-empty';
            empty.textContent = 'No entries yet — be the first!';
            return empty;
        }

        const table = document.createElement('div');
        table.className = 'leaderboard-table';

        // Group consecutive equal scores. The earliest submission leads the group at
        // its rank; any others collapse into an expandable "(N other players tied)" row.
        let i = 0;
        while (i < rows.length) {
            let j = i;
            while (j < rows.length && rows[j].score === rows[i].score) j++;
            const group = rows.slice(i, j);
            table.appendChild(this.renderRow(group[0], i + 1, me, isTotal));
            if (group.length > 1) {
                const key = `${this.activeTab}:${rows[i].score}`;
                const containsMe = group.some(r => r.entry.user_id === me);
                const expanded = containsMe || this.expandedTies.has(key);
                table.appendChild(this.renderTiedToggle(group.length - 1, expanded, key));
                if (expanded) {
                    for (const r of group.slice(1)) table.appendChild(this.renderTiedMember(r, me, isTotal));
                }
            }
            i = j;
        }
        return table;
    }

    /** Shared row body (name + score, with me/reference styling). */
    private makeRow(r: ScoredRow, me: string, isTotal: boolean): HTMLElement {
        const row = document.createElement('div');
        row.className = 'leaderboard-row';
        if (r.entry.user_id === me) row.classList.add('me');
        if (r.entry.username && isReservedUsername(r.entry.username)) row.classList.add('reference');

        const name = document.createElement('span');
        name.className = 'leaderboard-name';
        if (r.entry.username) {
            name.textContent = r.entry.username;
        } else {
            name.textContent = 'Anonymous';
            name.classList.add('anonymous');
        }

        const score = document.createElement('span');
        score.className = 'leaderboard-score';
        score.textContent = String(r.score);
        if (isTotal && r.entry.unsolved_count > 0) {
            const pen = document.createElement('span');
            pen.className = 'leaderboard-penalty';
            pen.textContent = ` (${r.entry.unsolved_count} unsolved)`;
            score.appendChild(pen);
        }

        row.append(name, score);
        return row;
    }

    private renderRow(r: ScoredRow, rank: number, me: string, isTotal: boolean): HTMLElement {
        const row = this.makeRow(r, me, isTotal);
        const rankEl = document.createElement('span');
        rankEl.className = 'leaderboard-rank';
        rankEl.textContent = String(rank);
        row.prepend(rankEl);
        return row;
    }

    private renderTiedMember(r: ScoredRow, me: string, isTotal: boolean): HTMLElement {
        const row = this.makeRow(r, me, isTotal);
        row.classList.add('tied-member');
        const spacer = document.createElement('span'); // empty rank cell, keeps alignment
        spacer.className = 'leaderboard-rank';
        row.prepend(spacer);
        return row;
    }

    private renderTiedToggle(count: number, expanded: boolean, key: string): HTMLElement {
        const row = document.createElement('div');
        row.className = 'leaderboard-tied-toggle';
        row.textContent = `${expanded ? '▾' : '▸'} ${count} other player${count === 1 ? '' : 's'} tied`;
        row.addEventListener('click', () => {
            if (this.expandedTies.has(key)) this.expandedTies.delete(key);
            else this.expandedTies.add(key);
            this.render();
        });
        return row;
    }

    private label(text: string): HTMLElement {
        const s = document.createElement('span');
        s.className = 'leaderboard-label';
        s.textContent = text;
        return s;
    }
}
